import type { ChangeEvent } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { t } from '../../i18n';
import type { WorkspaceStore } from '../../domain/store';

export interface FieldSpec<K extends string = string> {
  label: string;
  /** Key on the bound object. */
  key: K;
  maxLength?: number;
  type?: 'text' | 'email' | 'tel' | 'date' | 'number';
  required?: boolean;
  placeholder?: string;
  /** Render the value in the monospaced face — IDs, postcodes, points. */
  mono?: boolean;
  /** Full width inside a two-column grid. */
  wide?: boolean;
  options?: ReadonlyArray<readonly [value: string, label: string]>;
  hint?: string;
}

export interface FieldProps<T extends object> {
  /** Namespaces the generated `id` — cosmetic only, no data-attribute binding
   *  to look up anymore (that was `bindFields`' DOM-query mechanism). */
  scope: string;
  spec: FieldSpec<keyof T & string>;
  /** The live, mutable object this field reads from and writes into
   *  (`workspace.meta`, an `Athlete`, ...) — same object `useWorkspace` returns
   *  a fresh version-tick for, so writing into it directly here is safe.
   *  Typed as `T` rather than a `Record<string, unknown>` cast at every call
   *  site, so `spec.key` is checked against the target's real keys. */
  target: T;
  store: WorkspaceStore;
  /** Selects default to the "commit" path (controlled + `store.update`,
   *  triggering a re-render) since a selection often needs the rest of the
   *  screen to react. Plain text/tel/email/date inputs default to the
   *  "touch" path (uncontrolled + `store.touch`, no re-render) so typing
   *  never drops focus — see `src/domain/store.ts`'s `update`/`touch` split.
   *  Not every select needs a re-render either (e.g. Meldedaten's
   *  Landesverband) — pass `commit={false}` to opt a select out, or
   *  `commit={true}` to opt a text field in. */
  commit?: boolean;
  /** Extra side-effect logic for the commit path only (e.g. Starter's type
   *  select clearing dependent fields) — runs inside the same `store.update`
   *  right after the plain assignment. */
  onCommit?: (value: string) => void;
}

/** Radix `Select.Item` rejects an empty-string value outright — one option
 *  in this app's option lists (Sportler's "kein Verein") legitimately means
 *  "unset", so it's swapped for this sentinel on the way in/out. */
const EMPTY = ' empty';

export function Field<T extends object>({ scope, spec, target, store, commit, onCommit }: FieldProps<T>) {
  // Every value this component ever writes comes from an <input>/<select> —
  // a plain string — but a caller's real object type may narrow a field to
  // e.g. a union (`Sex`, `StarterType`, ...), and plain interfaces like
  // `Athlete`/`Starter` have no index signature, so `T` isn't structurally
  // assignable to `Record<string, unknown>` either. TypeScript can't prove
  // any of this generically, so this one cast is the internal escape hatch
  // that keeps every *call site* fully key-checked against `T` (the actual
  // point of making `Field` generic) while still allowing the string reads
  // and writes below.
  const rw = target as unknown as Record<string, unknown>;
  const id = `${scope}-${spec.key}`;
  const hint = spec.hint ?? (spec.maxLength !== undefined ? t('field.maxLength', { n: spec.maxLength }) : undefined);
  const useCommitPath = commit ?? spec.options !== undefined;
  const currentValue = String(rw[spec.key] ?? '');

  function handleTouchChange(e: ChangeEvent<HTMLInputElement>): void {
    rw[spec.key] = e.target.value;
    store.touch();
  }

  function handleCommitChange(e: ChangeEvent<HTMLInputElement>): void {
    const value = e.target.value;
    store.update(() => {
      rw[spec.key] = value;
      onCommit?.(value);
    });
  }

  function handleSelectChange(raw: string): void {
    const value = raw === EMPTY ? '' : raw;
    if (useCommitPath) {
      store.update(() => {
        rw[spec.key] = value;
        onCommit?.(value);
      });
    } else {
      rw[spec.key] = value;
      store.touch();
    }
  }

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', spec.wide && 'col-span-full')}>
      <Label htmlFor={id}>
        <span>
          {spec.label}
          {spec.required ? <span className="text-brand"> *</span> : null}
        </span>
        {hint ? <span className="font-mono text-[10px] font-normal text-muted-foreground">{hint}</span> : null}
      </Label>
      {spec.options !== undefined ? (
        <Select
          {...(useCommitPath
            ? { value: currentValue === '' ? EMPTY : currentValue }
            : { defaultValue: currentValue === '' ? EMPTY : currentValue })}
          onValueChange={handleSelectChange}
        >
          <SelectTrigger id={id} className={cn('w-full', spec.mono && 'font-mono')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {spec.options.map(([v, l]) => (
              <SelectItem key={v || EMPTY} value={v === '' ? EMPTY : v}>
                {l}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Input
          id={id}
          type={spec.type ?? 'text'}
          maxLength={spec.maxLength}
          placeholder={spec.placeholder}
          className={cn(spec.mono && 'font-mono')}
          {...(useCommitPath
            ? { value: currentValue, onChange: handleCommitChange }
            : { defaultValue: currentValue, onChange: handleTouchChange })}
        />
      )}
    </div>
  );
}
